import React from "react";
import { Dialog, Typography, Box } from "@mui/material";
import { Location, BilletGroup } from "../../../../models";
import {
  StyledDialogTitle,
  StyledDialogActions,
  LargeCancelButton,
} from "../../shared/components";

interface LocationSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (locationId: number) => void;
  locations: Location[];
  specialLocations: Location[];
  groupsByLocationId: { [key: number]: BilletGroup[] };
}

export const LocationSelectionModal = ({
  open,
  onClose,
  onLocationSelect,
  locations,
  specialLocations,
  groupsByLocationId,
}: LocationSelectionModalProps) => {
  // Group regular locations by pages (7 locations per page, 6 pages total)
  const LOCATIONS_PER_PAGE = 7;
  const TOTAL_PAGES = 6;

  const locationPages = Array.from({ length: TOTAL_PAGES }, (_, pageIndex) => {
    const startIndex = pageIndex * LOCATIONS_PER_PAGE;
    const endIndex = startIndex + LOCATIONS_PER_PAGE;
    return locations.slice(startIndex, endIndex);
  });

  const handleLocationClick = (locationId: number) => {
    onLocationSelect(locationId);
  };

  // Calculate total billets for a location
  const getTotalBillets = (locationId: number): number => {
    const groups = groupsByLocationId[locationId] || [];
    return groups.reduce(
      (total, group) => total + (group.BilletsCount || 0),
      0,
    );
  };

  // Render a location column
  const renderLocationColumn = (title: string, locationList: Location[]) => (
    <Box
      sx={{
        width: "14.28%", // 100% / 7 columns
        height: "100%",
        marginLeft: "0.25%",
        marginRight: "0.25%",
        background: (theme) =>
          `linear-gradient(145deg, white 0%, ${theme.palette.billetYard.backgrounds.lighter} 100%)`,
        border: (theme) =>
          `1px solid ${theme.palette.billetYard.borders.light}`,
        borderRadius: "12px",
        padding: "20px",
        overflowY: "auto",
        boxShadow: (theme) => theme.shadows[2],
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          color: (theme) => theme.palette.text.primary,
          marginBottom: "20px",
          paddingBottom: "16px",
          borderBottom: (theme) =>
            `2px solid ${theme.palette.billetYard.borders.light}`,
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          minHeight: "400px",
          padding: "8px",
        }}
      >
        {locationList.map((location) => {
          const totalBillets = getTotalBillets(location.LocationID);
          return (
            <Box
              key={location.LocationID}
              onClick={() => handleLocationClick(location.LocationID)}
              sx={{
                fontSize: "15px",
                minHeight: "60px",
                cursor: "pointer",
                padding: "12px 16px",
                borderRadius: "12px",
                transition: "all 0.2s ease",
                backgroundColor: "white",
                border: (theme) =>
                  `2px solid ${theme.palette.billetYard.borders.light}`,
                boxShadow: (theme) => theme.shadows[1],
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.billetYard.selected.background,
                  borderColor: (theme) =>
                    theme.palette.billetYard.selected.hover,
                  transform: "translateY(-2px)",
                  boxShadow: (theme) => theme.shadows[3],
                },
              }}
            >
              <Box
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.primary,
                  fontSize: "15px",
                  lineHeight: "1.3",
                }}
              >
                {location.HeaderText}
              </Box>
              <Box
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  fontSize: "13px",
                  marginTop: "4px",
                }}
              >
                Total: {totalBillets} billets
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <StyledDialogTitle colorScheme="primary">
        <Typography variant="h5" component="div">
          Select Location to Edit
        </Typography>
      </StyledDialogTitle>

      <Box
        sx={{
          height: "calc(100vh - 120px)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
            border: "none",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Column 1: Special Locations */}
          {renderLocationColumn("Special Locations", specialLocations)}

          {/* Columns 2-7: Regular Location Pages */}
          {locationPages.map((pageLocations, pageIndex) => (
            <React.Fragment key={`page-${pageIndex}`}>
              {renderLocationColumn(`Page ${pageIndex + 1}`, pageLocations)}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <StyledDialogActions>
        <LargeCancelButton variant="contained" onClick={onClose}>
          Cancel
        </LargeCancelButton>
      </StyledDialogActions>
    </Dialog>
  );
};
